/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { LocationUrls } from "../../model/navigation";
import { DeviceModel } from "../../model/device";
import { Log } from "../../viewModel/logging";
import {
  useUnityDispatch,
  useUnitySelector
} from "../../viewModel/UnityAppStore";
import {
  selectIsTokenRehydrating,
  selectIsAuthenticated,
  selectIsSignInErrorOverlayActive
} from "../../viewModel/signIn";
import {
  postSigninStartupSequence,
  selectHasStartedUp,
  selectIsProfileGateResolved,
  selectIsStarting,
  selectRequiresProfileSelection,
  selectStartupError
} from "../../viewModel/postSigninStartup";

const GUEST_ROUTE_REDIRECT_SUPPRESSED_ROUTES: ReadonlyArray<string> = [
  LocationUrls.PASSWORD_HTML,
  LocationUrls.FORGOT_PASSWORD,
  LocationUrls.REQUEST_NEW_LINK
];

const GUEST_ROUTE_REDIRECT_SUPPRESSED_PAGE_TYPES: ReadonlySet<string> =
  new Set<string>(["reset", "set"]);

function isGuestRouteRedirectSuppressed(
  pathname: string,
  search: string
): boolean {
  const pageType: string | null = new URLSearchParams(search).get("page");

  if (
    pageType !== null &&
    GUEST_ROUTE_REDIRECT_SUPPRESSED_PAGE_TYPES.has(pageType)
  ) {
    return true;
  }

  return GUEST_ROUTE_REDIRECT_SUPPRESSED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + "/")
  );
}

/**
 * Route guard for pre-auth (guest / public) routes such as the splash
 * screen and sign-in screen.
 *
 * This is the mirror of {@link ProtectedRoute}: it prevents an already
 * authenticated user from viewing an unprotected route. Once sign-in has
 * succeeded (or a token has been rehydrated), any attempt to land on a
 * public route — via the browser back button, a bookmark, or a typed URL —
 * is redirected forward to the protected area. On TV devices this is the
 * player; on browser (non-TV) devices it is the home screen.
 *
 * While the async IndexedDB token read is in progress (isTokenRehydrating),
 * renders nothing to avoid a flash-redirect in either direction.
 *
 * This guard redirects by *rendering* `<Navigate>`, which tears the current
 * screen down before the router — and therefore `NavigationInterceptor` —
 * ever sees a navigation. It is also the one guard whose branch can flip while
 * a sign-in error overlay is on screen: its children are the sign-in screens,
 * which is exactly where that overlay lives, and a background auth step (IP
 * auto-login, a DBS license-plate bind) can authenticate the device while the
 * user is still reading the error. So the forward redirect is held here too —
 * see the hold in {@link NavigationInterceptor} for the whole rationale.
 *
 * ## Holding in place while post-sign-in startup runs (UAW-2888)
 *
 * A user who signs in here is authenticated the moment `provisionDevicesV2`
 * lands, but the app is not usable until `postSigninStartupSequence` has
 * resolved device features, the profile gate and channels. Something must stay
 * on screen for that window, and it must be a *mounted screen*: input is routed
 * `activeScreen → ScreenController → modal stack`, so the CRITICAL "Connection
 * Error" overlay that startup raises can only take D-pad input while a screen
 * with a registered controller is up. `ProtectedRoute` deliberately renders
 * nothing during its own hold, so forwarding there immediately would leave that
 * overlay unreachable.
 *
 * This guard used to satisfy that by redirecting to {@link LocationUrls.SPLASH}
 * and letting `SplashScreen` own the wait. That worked, but it replayed the
 * startup splash *after* a successful sign-in — App Launch → Splash → Sign-In →
 * provisioning → Splash again → Home — which is not the product flow, and it
 * cost two extra navigations to get there.
 *
 * The screen the user is already standing on is the better waiting room: it is
 * mounted, it has registered its own `ScreenController`, and the sign-in slice
 * already keeps `isSigningIn` true past `signInWithCredentials.fulfilled`
 * ("until the screen unmounts") precisely so its inputs stay locked and its
 * progress indicator stays up. So the forward redirect is simply *held* until
 * startup resolves, and the user then makes a single transition from the
 * sign-in flow to its real destination. The splash screen keeps owning the
 * genuine startup wait — a cold start at `/` with a rehydrated token — which is
 * the only place it is reached from now.
 *
 * The hold ends on every outcome the sequence has: it completes
 * (`hasStartedUp`), it ends at the profile picker (`requiresProfileSelection`),
 * or it fails (`startupError`) — the last falling forward so the sequence's own
 * recovery path takes over, exactly as {@link ProtectedRoute} does.
 */
const GuestRoute: FunctionComponent = (): JSX.Element | null => {
  const TAG: string = "GuestRoute";

  const dispatch = useUnityDispatch();
  const isTokenRehydrating: boolean = useUnitySelector(
    selectIsTokenRehydrating
  );
  const isAuthenticated: boolean = useUnitySelector(selectIsAuthenticated);
  const isSignInErrorOverlayActive: boolean = useUnitySelector(
    selectIsSignInErrorOverlayActive
  );
  const hasStartedUp: boolean = useUnitySelector(selectHasStartedUp);
  const isStarting: boolean = useUnitySelector(selectIsStarting);
  const isProfileGateResolved: boolean = useUnitySelector(
    selectIsProfileGateResolved
  );
  const requiresProfileSelection: boolean = useUnitySelector(
    selectRequiresProfileSelection
  );
  const startupError: string | null = useUnitySelector(selectStartupError);
  const location = useLocation();
  const isRedirectSuppressed: boolean = isGuestRouteRedirectSuppressed(
    location.pathname,
    location.search
  );

  // True while an authenticated user is waiting here for startup to resolve.
  // Every terminal outcome clears it: fulfilment sets `hasStartedUp`, a profile
  // gate that needs the picker sets `isProfileGateResolved`, and a rejection
  // sets `startupError`.
  //
  // A live sign-in error overlay excludes the hold — not because the render
  // would differ (the overlay branch below returns the same `<Outlet />`) but
  // because it also gates the dispatch: startup raises its own CRITICAL
  // overlay through the shared alert slice, which would overwrite the error the
  // user is still reading. The sequence starts when that overlay clears.
  const shouldHoldForStartup: boolean =
    isAuthenticated &&
    !isRedirectSuppressed &&
    !isSignInErrorOverlayActive &&
    !hasStartedUp &&
    !isProfileGateResolved &&
    startupError === null;

  // Ends the hold above. Previously the splash redirect carried the user to
  // `SplashScreen`, which dispatched this; holding here means this guard has to
  // start it instead, or nothing would.
  //
  // `isRootEntry: true` matches the dispatch `SplashScreen` used to make on
  // this path, so the profile gate keeps treating a completed sign-in as a root
  // entry — a saved profile is auto-selected only when "skip" is enabled,
  // otherwise the picker is shown. The thunk's own `condition` makes the
  // sequence once-per-session, so this never races `ProtectedRoute`'s dispatch.
  useEffect((): void => {
    if (shouldHoldForStartup && !isStarting) {
      Log.d(
        TAG,
        "useEffect() authenticated on a guest route, dispatching postSigninStartupSequence"
      );
      dispatch(postSigninStartupSequence({ isRootEntry: true }));
    }
  }, [dispatch, shouldHoldForStartup, isStarting]);

  if (isTokenRehydrating) {
    return null;
  }

  if (isAuthenticated && !isRedirectSuppressed) {
    // Hold the redirect while a sign-in error overlay owns the screen: keep
    // rendering the child that is already mounted so the overlay stays
    // attached to its ScreenController and keeps taking input. The redirect
    // is not cancelled — this guard re-renders when the overlay clears and
    // takes the branch below then.
    if (isSignInErrorOverlayActive) {
      return <Outlet />;
    }
    // Hold on the screen the user signed in from until startup resolves, so no
    // splash is replayed between sign-in and the destination. Same shape as the
    // overlay hold above: the redirect is deferred, not cancelled.
    if (shouldHoldForStartup) {
      return <Outlet />;
    }
    // Startup finished at the profile picker — `hasStartedUp` stays false in
    // that case, so this has to be checked before the content redirect below.
    if (requiresProfileSelection) {
      return <Navigate to={LocationUrls.SELECT_PROFILE} replace />;
    }
    const target: string = DeviceModel.isTvDevice()
      ? LocationUrls.PLAYER
      : LocationUrls.HOME_SCREEN;
    return <Navigate to={target} replace />;
  }

  return <Outlet />;
};

export default GuestRoute;
